import React from "react";
import { Link } from "react-router-dom";
import { 
  Box, Typography, Stack, IconButton, 
  Divider, alpha, useTheme, Chip, CircularProgress 
} from "@mui/material";
import { 
  ArrowForwardIos, 
  AssignmentOutlined,
  CalendarToday,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { getAssignedFormsByClient } from "../../../api/services/Assessment/AssignAssessment";
import { QK } from "../../../base/qk";

const Assessments = () => {
  const theme = useTheme();
  const { id:clientID } = useParams();

  const { data: assignedForms, isLoading, error } = useQuery(
    [QK.assignedForms, clientID],
    () => getAssignedFormsByClient(clientID),
    { 
      enabled: !!clientID,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  );

  // Transform data for display
  const assessmentData = assignedForms?.map(form => ({
    title: form.form_title || form.title || "Untitled Assessment",
    date: form.assigned_at || form.created_at ? new Date(form.assigned_at || form.created_at).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) : "N/A",
    category: form.form_category || form.category || "Assessment",
    id: form.id,
    form_id: form.form || form.form_id,
    status: form.status || "active"
  })) || [];

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="error">
          Failed to load assessments
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box 
        sx={{ 
          px: 2,
          py: 1.5, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02)
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '6px',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AssignmentOutlined sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
          </Box>
          <Typography 
            variant="subtitle2" 
            fontWeight={600} 
            sx={{ color: '#1A1A2E', fontSize: '0.85rem' }}
          >
            Assessments
          </Typography>
          <Chip
            label={assessmentData.length}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.55rem',
              fontWeight: 500,
              bgcolor: '#F2F4F7',
              color: '#667085',
            }}
          />
        </Box>
        
        <IconButton 
          component={Link} 
          to={`/active-assessments/${clientID}`}
          size="small"
          sx={{ 
            width: 28,
            height: 28,
            bgcolor: '#fff', 
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            '&:hover': { 
              bgcolor: theme.palette.primary.main, 
              color: '#fff',
            },
            transition: 'all 0.2s',
          }}
        >
          <ArrowForwardIos sx={{ fontSize: 9 }} />
        </IconButton>
      </Box>

      <Divider sx={{ opacity: 0.4 }} />

      <Box sx={{ p: 1.5 }}>
        {assessmentData.length === 0 ? (
          <Box sx={{ 
            py: 3, 
            textAlign: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            borderRadius: '8px'
          }}>
            <AssignmentOutlined sx={{ 
              fontSize: 32, 
              color: alpha(theme.palette.primary.main, 0.3),
              mb: 1
            }} />
            <Typography variant="body2" sx={{ color: '#667085' }}>
              No assessments assigned yet
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.5}>
            {assessmentData.map((item) => (
              <Box 
                key={item.id}
                component={Link}
                to={`/active-assessments/${clientID}?form=${item.form_id}`}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.75,
                  px: 0.5,
                  borderRadius: '4px',
                  textDecoration: 'none',
                  '&:hover': { 
                    bgcolor: alpha(theme.palette.primary.main, 0.03) 
                  },
                  gap: 1,
                  cursor: 'pointer'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: '4px',
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <AssignmentOutlined sx={{ fontSize: 12, color: theme.palette.primary.main }} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#1A1A2E',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      flex: 1,
                    }}
                  >
                    {item.title}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarToday sx={{ fontSize: 11, color: '#98A2B3' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#667085',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                      }}
                    >
                      {item.date}
                    </Typography>
                  </Box>
                  <Chip
                    label={item.category}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.55rem',
                      fontWeight: 500,
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      color: theme.palette.primary.main,
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default Assessments;